import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { MultiChipSelect } from './campaign/MultiChipSelect';
import { RELATION_CONFIGS } from './relationConfigs';
import { getODataRows } from '../utils/odata';
import { formatTrPhone } from '../utils/phone';
import { isSuperAdmin, getTenantName, getUserId } from '../utils/auth';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldCustom, useIsViewMode,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
// KULLANICI_YETKI_YONETIMI §9 — kullanıcı kartının güvenlik tarafı sekmeleri.
// Ayrı dosyada: bu sayfa zaten 750+ satır ve dört yeni panel onu okunamaz hâle getirirdi.
import {
  UserSecurityTab, UserDevicesTab, UserDefaultsTab, UserActivityTab,
} from './detail/UserSecurityTabs';

const API_BASE = '/api/Users';

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

// #131 Veri yetkisi (data scope) türleri — backend DataScopeType ile birebir.
// Her tip, sistemde standart kullanılan RELATION_CONFIGS seçicisine bağlanır
// (Kritik SKU'daki MultiChipSelect ile aynı bileşen → datagrid'li arama/filtre/sayfalama).
// #280 Müşteri bazlı veri yetkisi tipleri (Grup/AltGrup/DinamikGrup/Müşteri) bu ekrandan
// kaldırıldı — Veri Yetkileri yalnız Distribütör / Temsilci / Depo ile sınırlandırılır.
// `vendorScoped`: seçenek kümesi seçili Distribütör yetkisine bağlıdır (Vendor.Id → X.VendorId).
// `autoSelect`: distribütör seçilince kapsamdaki TÜM kayıtlar otomatik işaretlenir (2026-07-16 kararı).
const SCOPE_DEFS = [
  { type: 1, label: 'Distribütör', cfg: 'vendor' },
  { type: 2, label: 'Satış Temsilcisi', cfg: 'representative', vendorScoped: true, endpoint: '/api/representatives' },
  { type: 3, label: 'Depo', cfg: 'warehouse', vendorScoped: true, endpoint: '/api/warehouses', autoSelect: true },
];
const DIST_SCOPE_TYPE = 1;
const VENDOR_SCOPED_DEFS = SCOPE_DEFS.filter((d) => d.vendorScoped);

// GEREKSİNİM 115: Mobil (saha) rol tespiti Role.Code VEYA Role.Name ile yapılır.
// Bazı tenant'larda Code anlamsız sıralı bir değerdir (örn. "03"), ayırt edici alan Name'dir.
// Backend ile birebir aynı küme tutulmalı:
// DataScope.Middleware/Security/Authorization/MobileRoleResolver.cs → MobileRoleTokens.
const MOBILE_ROLE_TOKENS = ['mobile', 'MOBILE', 'SALES_REP', 'SAHA', 'FIELD_REP', 'SAHA_SATIS', 'SATIS_TEMSILCISI'];
const MOBILE_ROLE_TOKEN_SET = new Set(MOBILE_ROLE_TOKENS.map((t) => t.toLowerCase()));
const isMobileToken = (token) =>
  !!token && MOBILE_ROLE_TOKEN_SET.has(String(token).trim().toLowerCase());
const isMobileRoleObj = (role) =>
  !!role && (isMobileToken(role.code ?? role.Code) || isMobileToken(role.name ?? role.Name));

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phoneNumber: '',
  isActive: 'true',
  roleIds: [],
  representativeId: '',
  isDistributor: 'false',
  // G-K-01 / KK-04 — sınıflandırma grubu. Yetki TAŞIMAZ; raporlama kırılımı ve KK-16'daki
  // giriş gün/saat kısıtının "Grup" kriteri bu alanı okur.
  userGroupId: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

// Backend bazı hataları insan-okunur metin yerine kod döner (örn. "DuplicateEmailError").
// Bunları anlamlı Türkçe mesaja çevir; geri kalan mesajlar (şifre politikası, doğrulama,
// temsilci ataması) zaten Türkçe geldiği için olduğu gibi yüzeye çıkar.
const USER_ERROR_MESSAGES = {
  DuplicateEmailError: 'Bu e-posta adresi zaten kayıtlı. Lütfen farklı bir e-posta girin.',
};
function translateUserError(raw) {
  if (!raw) return '';
  return USER_ERROR_MESSAGES[raw] || raw;
}

// #278 Kullanıcının TÜM rollerini (roleId listesi) döner — çoklu rol desteği.
function getUserRoleIds(user) {
  const ur = pick(user, 'userRole', 'UserRole');
  if (Array.isArray(ur)) return [...new Set(ur.map((x) => String(pick(x, 'roleId', 'RoleId') ?? '')).filter(Boolean))];
  return [];
}

function rowToForm(row) {
  const repId = pick(row, 'representativeId', 'RepresentativeId');
  return {
    firstName: pick(row, 'firstName', 'FirstName') ?? '',
    lastName: pick(row, 'lastName', 'LastName') ?? '',
    email: pick(row, 'email', 'Email') ?? '',
    password: '',
    phoneNumber: pick(row, 'phoneNumber', 'PhoneNumber') ?? '',
    isActive: (pick(row, 'isActive', 'IsActive') ?? false) ? 'true' : 'false',
    roleIds: getUserRoleIds(row),
    representativeId: repId ? String(repId) : '',
    isDistributor: (pick(row, 'isDistributor', 'IsDistributor') ?? false) ? 'true' : 'false',
    userGroupId: (() => {
      const g = pick(row, 'userGroupId', 'UserGroupId');
      return g == null ? '' : String(g);
    })(),
  };
}

export function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const isView = useIsViewMode() && !isNew; // #282 izle = salt-okunur
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Kullanıcı' : (isView ? 'Kullanıcı Görüntüle' : 'Kullanıcı Detayı'), 'Kullanıcıları, rollerini ve durum bilgilerini yönetin.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [roles, setRoles] = useState([]);
  // GEREKSİNİM 115: mobil rol seçiliyken doldurulan temsilci seçenekleri (unassigned).
  const [representatives, setRepresentatives] = useState([]);
  // Düzenleme/silme için yüklenen ham kayıt (Delete ucu tüm kaydı ister).
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // #184 Kullanıcının bağlı olduğu Tenant — köşede info rozeti olarak gösterilir.
  const [tenantLabel, setTenantLabel] = useState('');

  // #131 Veri Yetkileri sekmesi
  const [tab, setTab] = useState('info');
  const [userGroups, setUserGroups] = useState([]); // G-K-01 sınıflandırma grupları (lookup)
  const [scopeItems, setScopeItems] = useState({}); // { [type]: [{value,label}] } — seçili yetkiler
  const [savingScopes, setSavingScopes] = useState(false);
  // Distribütöre bağlı seçenek kümeleri: { [type]: [ham OData satırı] } (VendorId ile daraltılır).
  const [vendorScopedRows, setVendorScopedRows] = useState({});
  // Aday listeleri sunucudan geldi mi? "Yüklenmedi" ile "yüklendi ama boş" AYRI şeylerdir:
  // ikincisinde kayıtlı yetkiler doğrulanamıyor demektir ve uyarı gösterilmelidir.
  const [lookupLoaded, setLookupLoaded] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Roller küçük bir lookup — tümünü /Select ile yükle (FieldSelect seçenekleri).
  useEffect(() => {
    fetch('/api/Roles/Select', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((json) => {
        const data = json?.data ?? json ?? [];
        setRoles(Array.isArray(data) ? data.filter((role) => role.isActive) : []);
      })
      .catch(() => {});
  }, []);

  // G-K-01 — kullanıcı grupları (sınıflandırma tanımı). Küçük master; tümü çekilir.
  useEffect(() => {
    fetch('/api/user-groups?$orderby=Name&$top=500', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const data = json?.value ?? json?.data ?? json ?? [];
        setUserGroups(Array.isArray(data) ? data.filter((g) => (g.isActive ?? g.IsActive ?? true)) : []);
      })
      .catch(() => setUserGroups([]));
  }, []);

  const userGroupOptions = [
    { value: '', label: 'Grup yok' },
    ...userGroups.map((g) => {
      const code = pick(g, 'code', 'Code') ?? '';
      const name = pick(g, 'name', 'Name') ?? '';
      return { value: String(pick(g, 'id', 'Id')), label: code ? `${code} - ${name}` : name };
    }),
  ];

  // GEREKSİNİM 115: seçili rol mobil (saha) mı? Tespit Role.Code VEYA Role.Name ile yapılır.
  // #278 Çoklu rol: seçili rollerden HERHANGİ BİRİ mobil (saha) ise temsilci alanı görünür.
  const selectedRoles = roles.filter((item) => form.roleIds.includes(String(item.id)));
  const isMobileRole = selectedRoles.some(isMobileRoleObj);
  // Yüklü rol chip etiketi (Kod - Ad); config.getRowLabel ile tutarlı.
  const roleName = (rid) => {
    const r = roles.find((x) => String(x.id) === String(rid));
    if (!r) return String(rid);
    const code = r.code ?? r.Code ?? '';
    return code ? `${code} - ${r.name}` : r.name;
  };

  const editingUserId = record?.id ?? record?.Id ?? null;

  // Mobil rol seçiliyse atanmamış temsilcileri yükle (düzenlenen user'ın mevcut temsilcisi dahil).
  // Rol mobil değilse seçimi temizle (alan gizlenir, payload'a null gider).
  useEffect(() => {
    if (!isMobileRole) {
      setRepresentatives([]);
      if (form.representativeId) set('representativeId', '');
      return;
    }
    const qs = editingUserId ? `?userId=${editingUserId}` : '';
    fetch(`/api/representatives/unassigned${qs}`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((json) => {
        const data = json?.data ?? json ?? [];
        setRepresentatives(Array.isArray(data) ? data : []);
      })
      .catch(() => setRepresentatives([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileRole, editingUserId]);

  const representativeOptions = [
    { value: '', label: 'Temsilci seçiniz (opsiyonel)' },
    ...representatives.map((rep) => ({
      value: String(rep.id ?? rep.Id),
      label: rep.name ?? rep.Name ?? (rep.textId ?? rep.TextId ?? ''),
    })),
  ];

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    // Listeden taşınan satır (location.state.row) YALNIZCA UserRole expand'ini
    // içeriyorsa doğrudan kullanılabilir. Liste OData sorgusu UserRole'ü $expand
    // ETMEZ, bu yüzden taşınan satırda rol bilgisi yoktur → forma boş roleId gelir
    // ve rol ile belirlenen "MOBİL" kullanıcı tipi sıfırlanmış görünür (#199).
    // Bu durumda aşağıda tam kaydı $expand=UserRole ile çekeriz.
    // NOT: $expand=UserRole backend'de dolu koleksiyonda asılabiliyordu; kök düzeltme
    // ODataResponse.DeepConvert (backend) ile yapıldı (bkz. [[odata-expand-userrole-hang]]).
    const stateRow = location.state?.row;
    if (stateRow && pick(stateRow, 'userRole', 'UserRole') !== undefined) {
      setRecord(stateRow);
      setForm(rowToForm(stateRow));
      // Nav-state satırı liste OData $select'inden gelir; audit alanlarını (createdDate/By…)
      // İÇERMEZ → info butonu gizli kalır. Tam kaydı arka planda çekip audit'i ondan doldur
      // (paint'i bloklamaz).
      fetch(`${API_BASE}?$filter=Id eq ${id}&$expand=UserRole&$top=1`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => {
          const items = json?.value ?? json?.data ?? json ?? [];
          const rec = Array.isArray(items) ? items[0] : items;
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const url = `${API_BASE}?$filter=Id eq ${id}&$expand=UserRole&$top=1`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kullanıcı alınamadı (${res.status})`);
      const json = await res.json();
      const items = json?.value ?? json?.data ?? json ?? [];
      const r = Array.isArray(items) ? items[0] : items;
      if (!r) throw new Error('Kullanıcı bulunamadı');
      setRecord(r);
      setForm(rowToForm(r));
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Kullanıcı alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // #184 Kullanıcının bağlı olduğu Tenant'ı çöz → köşede info rozeti.
  // Yeni kayıtta kullanıcı admin'in kendi tenant'ına oluşturulur (token'daki ad).
  // Düzenlemede kaydın TenantId'si kullanılır; SUPERADMIN ise id → gerçek tenant adına çevrilir
  // (Tenants/Select yalnız SuperAdmin'e açık), normal admin yalnız kendi tenant'ını gördüğü
  // için token'daki ad yeterlidir.
  useEffect(() => {
    const tokenName = getTenantName();
    if (isNew) { setTenantLabel(tokenName || ''); return; }
    const rawTenantId = pick(record, 'tenantId', 'TenantId');
    if (!rawTenantId) { setTenantLabel(tokenName || ''); return; }
    if (!isSuperAdmin()) { setTenantLabel(tokenName || String(rawTenantId)); return; }
    let alive = true;
    fetch('/api/Tenants/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive) return;
        const list = j?.data ?? j?.value ?? j ?? [];
        const t = (Array.isArray(list) ? list : []).find(
          (x) => String(x.id ?? x.Id) === String(rawTenantId));
        setTenantLabel(t ? (t.name ?? t.Name ?? String(rawTenantId)) : String(rawTenantId));
      })
      .catch(() => { if (alive) setTenantLabel(String(rawTenantId)); });
    return () => { alive = false; };
  }, [record, isNew]);

  // Kayıtlı id listesinin etiketlerini ilgili relation endpoint'inden çöz (chip gösterimi için).
  const resolveLabels = useCallback(async (cfg, ids) => {
    if (!ids.length) return [];
    const config = RELATION_CONFIGS[cfg];
    const clause = ids.slice(0, 100).map((x) => `Id eq ${x}`).join(' or ');
    const url = `${config.endpoint}?$top=${ids.length}&$filter=${encodeURIComponent(clause)}`;
    try {
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) throw new Error();
      const rows = getODataRows(await r.json());
      const byId = {};
      rows.forEach((row) => { byId[String(row.id ?? row.Id)] = config.getRowLabel(row); });
      return ids.map((x) => ({ value: String(x), label: byId[String(x)] || String(x) }));
    } catch {
      return ids.map((x) => ({ value: String(x), label: String(x) }));
    }
  }, []);

  // #131 Veri Yetkileri sekmesi: kullanıcının mevcut yetkilerini yükle + etiketlerini çöz.
  useEffect(() => {
    if (tab !== 'data' || isNew || !id) return;
    fetch(`${API_BASE}/${id}/datascopes`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const items = j?.data ?? j?.value ?? j ?? [];
        const byType = {};
        (Array.isArray(items) ? items : []).forEach((it) => {
          const t = it.scopeType ?? it.ScopeType;
          const sid = it.scopeId ?? it.ScopeId;
          if (t == null || sid == null) return;
          (byType[t] = byType[t] || []).push(String(sid));
        });
        // Her tip için etiketleri çöz, sonra scopeItems'a yaz.
        Promise.all(SCOPE_DEFS.map((d) =>
          resolveLabels(d.cfg, byType[d.type] || []).then((its) => [d.type, its]),
        )).then((pairs) => setScopeItems(Object.fromEntries(pairs)));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id, isNew]);

  // Distribütöre bağlı türlerin (Temsilci/Depo) TÜM adaylarını bir kez çek; seçenek listesi ve
  // otomatik seçim bunlardan hesaplanır. VendorId olmadan hangi kaydın hangi distribütöre ait
  // olduğu bilinemez, bu yüzden $select'te zorunlu.
  useEffect(() => {
    if (tab !== 'data') return;
    let cancelled = false;
    Promise.all(VENDOR_SCOPED_DEFS.map(async (d) => {
      try {
        const url = `${d.endpoint}?$select=Id,TextId,Name,VendorId&$orderby=TextId&$top=1000`;
        const r = await fetch(url, { headers: authHeaders() });
        if (!r.ok) throw new Error();
        return [d.type, getODataRows(await r.json())];
      } catch {
        return [d.type, []];
      }
    })).then((pairs) => {
      if (cancelled) return;
      setVendorScopedRows(Object.fromEntries(pairs));
      setLookupLoaded(true);
    });
    return () => { cancelled = true; };
  }, [tab]);

  // #131 Distribütör tipi kullanıcı = açık bayrak (User.IsDistributor). True ise en fazla 1 distribütör yetkisi.
  const isDistributorUser = form.isDistributor === 'true';

  const rowVendorId = (row) => String(row?.vendorId ?? row?.VendorId ?? '');
  const rowId = (row) => String(row?.id ?? row?.Id ?? '');
  const rowToItem = (cfg, row) => ({ value: rowId(row), label: RELATION_CONFIGS[cfg].getRowLabel(row) });

  // Seçili distribütörler — Temsilci/Depo seçeneklerinin kapsamını belirler.
  const selectedVendorIds = useMemo(
    () => new Set((scopeItems[DIST_SCOPE_TYPE] || []).map((it) => String(it.value))),
    [scopeItems],
  );

  // Bir tür için seçilebilir satırlar: yalnız seçili distribütörlere ait olanlar.
  // Distribütör seçili değilse kapsam boştur (AND mantığı: üst eksen seçilmeden alt eksen verilemez).
  const rowsForType = useCallback((type) => {
    const all = vendorScopedRows[type] || [];
    return all.filter((row) => selectedVendorIds.has(rowVendorId(row)));
  }, [vendorScopedRows, selectedVendorIds]);

  // Kayıtlı ama artık kapsam dışı kalan seçimler (ör. distribütörü değişmiş bir depo).
  // Sessizce atmıyoruz — kullanıcı neyin neden düşeceğini görmeli.
  const staleItemsForType = useCallback((type) => {
    // Aday listesi HENÜZ YÜKLENMEDİYSE hiçbir şey iddia etme (yükleme sırasında her satır
    // yanlışlıkla "kapsam dışı" görünürdü). Yüklendiyse — BOŞ olsa bile — kayıtlı satırlar
    // doğrulanamıyor demektir ve kullanıcı bunu görmeli.
    //
    // Eskiden burada `if (all.length === 0) return []` vardı: aday listesi boş olduğunda uyarı
    // tam da EN GEREKLİ olduğu anda susuyordu. Canlı vaka: sutas@sutas'ın kayıtlı temsilcisi
    // (Marmara) seçili distribütörün (Ege) dışında olduğu için aday listesi boş dönüyor, ekran
    // da "temsilci yetkisi yok" gibi görünüyordu — oysa arkada veriyi sıfırlayan satır duruyordu.
    if (!lookupLoaded) return [];
    const inScope = new Set(rowsForType(type).map(rowId));
    return (scopeItems[type] || []).filter((it) => !inScope.has(String(it.value)));
  }, [lookupLoaded, rowsForType, scopeItems]);

  const setScope = (type, items) => setScopeItems((prev) => ({ ...prev, [type]: items }));

  // Distribütör değişimi alt eksenleri yeniden kurar:
  //   - Depo (autoSelect): kapsamdaki TÜM depolar otomatik işaretlenir.
  //   - Temsilci: seçim kullanıcıya ait; yalnız kapsam dışı kalanlar düşer.
  const setDistributorScope = (items) => {
    const next = isDistributorUser ? items.slice(-1) : items;
    const vendorIds = new Set(next.map((it) => String(it.value)));
    setScopeItems((prev) => {
      const updated = { ...prev, [DIST_SCOPE_TYPE]: next };
      VENDOR_SCOPED_DEFS.forEach((d) => {
        const inScopeRows = (vendorScopedRows[d.type] || []).filter((row) => vendorIds.has(rowVendorId(row)));
        if (d.autoSelect) {
          updated[d.type] = inScopeRows.map((row) => rowToItem(d.cfg, row));
        } else {
          const inScopeIds = new Set(inScopeRows.map(rowId));
          updated[d.type] = (prev[d.type] || []).filter((it) => inScopeIds.has(String(it.value)));
        }
      });
      return updated;
    });
  };

  const saveScopes = async () => {
    if (isNew) { showToast('error', 'Önce kullanıcıyı kaydedin.'); return; }
    const items = [];
    Object.entries(scopeItems).forEach(([type, its]) =>
      (its || []).forEach((it) => items.push({ scopeType: Number(type), scopeId: it.value })));
    setSavingScopes(true);
    try {
      const res = await fetch(`${API_BASE}/datascopes`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ userId: Number(record?.id ?? record?.Id ?? id), items }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || pick(body, 'success', 'Success') === false) throw new Error(pick(body, 'message', 'Message') || 'Yetkiler kaydedilemedi');
      showToast('success', 'Veri yetkileri kaydedildi.');
    } catch (err) {
      showToast('error', err.message || 'Yetkiler kaydedilemedi');
    } finally {
      setSavingScopes(false);
    }
  };

  // Klonla: /user-management/new'e kopya veriyle gelindiyse formu doldur
  // (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar). location.key her
  // navigasyonda değiştiği için bir klon yalnızca bir kez uygulanır.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  // Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.firstName.trim()) { showToast('error', 'Ad zorunludur'); return; }
    if (!form.lastName.trim()) { showToast('error', 'Soyad zorunludur'); return; }
    if (!form.email.trim()) { showToast('error', 'E-posta zorunludur'); return; }
    // #275 Rol zorunlu alan (#278 en az bir rol).
    if (!form.roleIds.length) { showToast('error', 'En az bir rol seçilmelidir'); return; }
    // Şifre artık girilmez: yeni kullanıcıda sistem otomatik geçici şifre üretip e-posta ile gönderir
    // ve kullanıcı ilk girişte değiştirmek zorundadır (MustChangePassword).

    // #278 Seçili tüm roller gönderilir; backend UpdateUser diff'leyerek ekler/siler.
    const userRole = form.roleIds.map((rid) => ({ userId: isNew ? 0 : (record?.id ?? record?.Id ?? 0), roleId: Number(rid) }));
    // GEREKSİNİM 115: temsilci yalnız mobil rolde gönderilir; aksi halde null.
    // (Backend de aynı normalleştirmeyi yapar; bu yalnız tutarlı bir payload içindir.)
    const representativeId = isMobileRole && form.representativeId ? form.representativeId : null;
    const payload = isNew
      ? {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          // password gönderilmez → backend tenant politikasına uygun geçici şifre üretir + mail'ler.
          phoneNumber: form.phoneNumber,
          isActive: true,
          representativeId,
          isDistributor: form.isDistributor === 'true',
          // G-K-01 — grup kullanıcıyla AYNI kayıtta yazılır (User'ın kendi kolonu). Güncellemede
          // GÖNDERİLMEZ: orada aynı DTO mevcut kaydın üzerine map'lendiği için alan grubu
          // sessizce silme riski taşır; değişim ayrı uçtan yapılır.
          userGroupId: form.userGroupId ? Number(form.userGroupId) : null,
          userRole,
          userRight: [],
          pageUserActionRight: [],
        }
      : {
          id: record?.id ?? record?.Id,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          password: form.password || null,
          phoneNumber: form.phoneNumber,
          isActive: form.isActive === 'true',
          representativeId,
          isDistributor: form.isDistributor === 'true',
          userRole,
          userRight: [],
          pageUserActionRight: [],
        };

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Backend hata mesajını (e-posta zaten kayıtlı, şifre politikası, doğrulama, temsilci
        // ataması vb.) yüzeye çıkar — jenerik "eklenemedi" yerine gerçek nedeni göster.
        const raw = pick(body, 'Message', 'message');
        throw new Error(translateUserError(raw) || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      }
      if (isNew) {
        if (andNew) {
          showToast('success', 'Kullanıcı eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Kullanıcı eklendi.');
        setTimeout(() => navigate('/user-management'), 400);
        return;
      }
      // G-K-01 · KK-04 — Kullanıcı grubu AYRI uçtan yazılır. Sebebi UserForRegisterDto'ya alan
      // eklememek: o DTO mevcut kayda AutoMapper ile map'leniyor, alan eklenseydi grubu
      // göndermeyen her istemci kullanıcının grubunu sessizce silerdi
      // (bkz. UserAdminController.SetUserGroup). Yalnız DEĞİŞTİYSE çağrılır.
      const originalGroupId = rowToForm(record ?? {}).userGroupId;
      if (form.userGroupId !== originalGroupId) {
        const targetId = record?.id ?? record?.Id;
        const groupRes = await fetch(`${API_BASE}/${targetId}/user-group`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(form.userGroupId
            ? { userGroupId: Number(form.userGroupId) }
            : { clearUserGroup: true }),
        });
        if (!groupRes.ok) {
          const gb = await groupRes.json().catch(() => ({}));
          // Ana kayıt yazıldı ama grup yazılamadı — sessizce geçmek yerine uyar ve ekranda kal,
          // aksi halde kullanıcı grubu kaydolmuş sanır.
          showToast('error', pick(gb, 'Message', 'message') || 'Kullanıcı grubu kaydedilemedi.');
          return;
        }
      }

      // GÜVENLİK: Yenilenen token'ı oturuma YALNIZCA kullanıcı KENDİ hesabını düzenlediğinde
      // yaz. Admin başka bir kullanıcıyı düzenlerken dönen token o kullanıcıya aittir; onu
      // oturum anahtarına yazmak admin'in oturumunu düzenlenen kullanıcıya çevirir (oturum
      // ele geçirme). Backend zaten yalnızca kendi hesap düzenlemesinde token döner; bu
      // istemci tarafı ikinci bir savunma katmanıdır.
      const newToken = body?.data?.token;
      const editedId = record?.id ?? record?.Id;
      if (newToken && editedId != null && editedId === getUserId()) {
        localStorage.setItem('token', newToken);
      }
      showToast('success', 'Kullanıcı güncellendi.');
      setTimeout(() => navigate('/user-management'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene kadar yeni kullanıcı oluşmaz.
  const handleClone = () => {
    navigate('/user-management/new', { state: { clone: { ...form, password: '' } } });
  };

  const handleDelete = async () => {
    if (!record) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(record),
      });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Kullanıcı silindi.');
      setTimeout(() => navigate('/user-management'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Kullanıcı' : (`${form.firstName} ${form.lastName}`.trim() || 'Kullanıcı Detayı');
  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="bad">Pasif</StatusBadge>);

  // #184 Bağlı olduğu Tenant info rozeti (durum rozetinin yanında, sağ üst köşede).
  const tenantChip = tenantLabel ? (
    <span className="dps-info-chip" title={`Bağlı olduğu Tenant: ${tenantLabel}`}>
      <Icon name="info" size={14} /> Tenant: <b>{tenantLabel}</b>
    </span>
  ) : null;
  const headerStatus = (statusBadge || tenantChip) ? (<>{statusBadge}{tenantChip}</>) : null;

  // #282 İzle modunda salt-okunur: yalnız "Kapat" + "Düzenle".
  const actions = isView ? (
    <>
      <button className="dps-btn ghost" onClick={() => navigate('/user-management')}>Kapat</button>
      <button className="dps-btn primary" onClick={() => navigate(`/user-management/${id}`, { state: { row: location.state?.row, view: false }, replace: true })}>Düzenle</button>
    </>
  ) : (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/user-management')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni kullanıcı aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      readOnly={isView}
      crumb={[{ label: 'Kullanıcı Tanımları', to: '/user-management' }, { label: isNew ? 'Yeni Kullanıcı' : (isView ? 'Kullanıcı Görüntüle' : 'Kullanıcı Detayı'), current: true }]}
      status={headerStatus}
      tabs={[
        { key: 'info', label: 'Kullanıcı Bilgileri', icon: 'user' },
        { key: 'data', label: 'Veri Yetkileri', icon: 'shield', disabled: isNew, title: isNew ? 'Önce kullanıcıyı kaydedin' : undefined },
        // Yeni sekmeler kayıtlı kullanıcı gerektirir: hepsi kullanıcı Id'siyle sunucudan veri çeker.
        { key: 'security', label: 'Güvenlik', icon: 'lock', disabled: isNew, title: isNew ? 'Önce kullanıcıyı kaydedin' : undefined },
        { key: 'defaults', label: 'Ön Değerler', icon: 'sliders', disabled: isNew, title: isNew ? 'Önce kullanıcıyı kaydedin' : undefined },
        { key: 'devices', label: 'Cihazlar', icon: 'box', disabled: isNew, title: isNew ? 'Önce kullanıcıyı kaydedin' : undefined },
        { key: 'activity', label: 'Hareketler', icon: 'clock', disabled: isNew, title: isNew ? 'Önce kullanıcıyı kaydedin' : undefined },
      ]}
      activeTab={tab}
      onTab={setTab}
      // "Yetkileri Kaydet" YALNIZ veri yetkisi sekmesine aittir. Yeni sekmeler (Güvenlik, Ön
      // Değerler, Cihazlar, Hareketler) kendi kaydetme aksiyonlarını kendi panellerinde taşır;
      // buraya düşerlerse yanlış sekmede yanlış kaydet butonu görünürdü.
      actions={tab === 'data' && !isView ? (
        <>
          <button className="dps-btn ghost" onClick={() => navigate('/user-management')} disabled={savingScopes}>İptal</button>
          <button className="dps-btn primary" onClick={saveScopes} disabled={savingScopes}>
            <Icon name="check" size={17} /> {savingScopes ? 'Kaydediliyor…' : 'Yetkileri Kaydet'}
          </button>
        </>
      ) : (tab === 'info' || isView ? actions : null)}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'User', entityId: id }}
      loading={loading}
    >
      {tab === 'info' && (
      <div className="dps-form">
        <Section icon="user" title="Kullanıcı Bilgileri" subtitle="Kimlik ve iletişim bilgileri">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="firstName" label="Ad" req placeholder="Ad giriniz" />
            <FieldText form={form} set={set} tried={tried} k="lastName" label="Soyad" req placeholder="Soyad giriniz" />
            <FieldText form={form} set={set} tried={tried} k="email" label="E-posta" req type="email" placeholder="E-posta giriniz" hint="(kullanıcı adı olarak kullanılacaktır)" />
            <FieldText form={form} set={set} k="phoneNumber" label="Telefon" placeholder="(0532) 222 22 22" format={formatTrPhone} />
          </Grid>
        </Section>

        <Section icon="shield" title="Erişim & Durum" subtitle="Rol, şifre ve aktiflik bilgileri">
          <Grid cols={2}>
            {/* #278 Çoklu rol — Kritik SKU'daki MultiChipSelect (config modu: datagrid seçince kapanmaz,
                birden fazla rol art arda eklenir, seçili olanlar işaretli; chip/× ile çıkar). */}
            <FieldCustom label="Roller" req invalid={tried && form.roleIds.length === 0}>
              <MultiChipSelect
                items={form.roleIds.map((rid) => ({ value: rid, label: roleName(rid) }))}
                onChange={(its) => setForm((c) => ({ ...c, roleIds: its.map((x) => String(x.value)) }))}
                config={RELATION_CONFIGS.role}
                placeholder="Rol ara ve ekle…"
                disabled={isView}
              />
            </FieldCustom>
            <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
            {/* G-K-01 · KK-04 — Sınıflandırma grubu. Rol/yetki TAŞIMAZ; Müşteri Grubu / Ürün Grubu
                ile aynı kalıptır. Ayrıca Genel Parametreler'deki giriş gün/saat kısıtı (KK-16)
                "Grup" kriteri seçildiğinde bu alanı okur.

                Yeni kayıtta da SEÇİLEBİLİR: grup User tablosunun kendi kolonu olduğu için
                kullanıcıyla aynı INSERT'te yazılır. Eskiden "kayıttan sonra seçilebilir" deniyordu
                çünkü grup ayrı uçtan (PUT /{id}/user-group) yazılıyordu ve o uç kullanıcının
                Id'sini istiyordu — Add ucu ise yeni Id'yi dönmüyordu. Artık kayıtta ikinci bir
                çağrı yok, dolayısıyla "kullanıcı oluştu ama grup yazılamadı" durumu da yok. */}
            <FieldSelect
              form={form}
              set={set}
              k="userGroupId"
              label="Kullanıcı Grubu"
              options={userGroupOptions}
              hint="(yetki taşımaz — raporlama kırılımı ve giriş saati kriteri için)"
            />
            {/* #131 Distribütör tipi kullanıcı → Veri Yetkileri'nde en fazla 1 distribütör seçilebilir. */}
            <FieldSelect
              form={form}
              set={set}
              k="isDistributor"
              label="Distribütör Tipi Kullanıcı"
              options={[{ value: 'false', label: 'Hayır' }, { value: 'true', label: 'Evet' }]}
            />
            {/* GEREKSİNİM 115: yalnız mobil (saha) rolde görünür opsiyonel Temsilci eşleşmesi. */}
            {isMobileRole && (
              <FieldSelect
                form={form}
                set={set}
                k="representativeId"
                label="Satış Temsilcisi"
                options={representativeOptions}
              />
            )}
            {isNew ? (
              <FieldCustom label="Şifre">
                <div style={{
                  fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6,
                  background: 'var(--surface-2, #f5f7fb)', border: '1px solid var(--border, #e2e8f0)',
                  borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'flex-start',
                }}>
                  <Icon name="info" size={16} />
                  <span>Şifre <strong>otomatik oluşturulup</strong> kullanıcının e-posta adresine gönderilecek.
                    Kullanıcı ilk girişte kendi şifresini belirleyecektir.</span>
                </div>
              </FieldCustom>
            ) : (
              <FieldText
                form={form}
                set={set}
                tried={tried}
                k="password"
                label="Şifre (opsiyonel)"
                type="password"
                placeholder="Değiştirmek için girin"
              />
            )}
          </Grid>
        </Section>
      </div>
      )}

      {tab === 'data' && (
        <div className="dps-form">
          <Section icon="shield" title="Veri Yetkileri"
            subtitle="Kullanıcının görebileceği veriyi sınırlayın. Hiç yetki verilmezse (admin hariç) müşteri listesi boş gelir.">
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Seçilen yetkilerin <strong>kesişimi</strong> uygulanır: veri, seçim yapılmış <strong>her</strong>
              {' '}eksenle eşleşmelidir. Önce <strong>Distribütör</strong> seçin — Satış Temsilcisi ve Depo
              listeleri yalnız o distribütörün kayıtlarını gösterir, depolar otomatik işaretlenir.
              {isDistributorUser ? <> Distribütör tipi kullanıcıya yalnızca <strong>1 distribütör</strong> verilebilir.</> : null}
            </p>
            <Grid cols={2}>
              {SCOPE_DEFS.map((d) => {
                const sel = scopeItems[d.type] || [];
                const single = d.type === DIST_SCOPE_TYPE && isDistributorUser;
                const isDist = d.type === DIST_SCOPE_TYPE;
                // Distribütör seçilmeden alt eksen seçilemez (kapsam boş olurdu).
                const blocked = !isDist && selectedVendorIds.size === 0;
                const stale = isDist ? [] : staleItemsForType(d.type);
                return (
                  <div key={d.type} style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
                      {d.label}{single ? ' (tek seçim)' : ''}
                      {sel.length > 0 ? <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> · {sel.length} seçili</span> : null}
                    </div>
                    {blocked ? (
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        Önce distribütör seçin.
                      </span>
                    ) : (
                      <MultiChipSelect
                        items={sel}
                        onChange={(its) => (isDist ? setDistributorScope(its) : setScope(d.type, its))}
                        config={RELATION_CONFIGS[d.cfg]}
                        rows={isDist ? undefined : rowsForType(d.type)}
                        placeholder={`${d.label} ara ve ekle…`}
                        disabled={isView}
                      />
                    )}
                    {stale.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-warning, #a15c18)' }}>
                        Seçili distribütör kapsamı dışında {stale.length} kayıt var
                        ({stale.map((it) => it.label).join(', ')}). Kaydedilse bile veri getirmez; kaldırın.
                      </div>
                    )}
                  </div>
                );
              })}
            </Grid>
          </Section>
        </div>
      )}

      {/* KULLANICI_YETKI_YONETIMI §9 — güvenlik tarafı sekmeleri. Hepsi kayıtlı kullanıcı
          gerektirir; sekme başlıkları isNew iken zaten disabled. */}
      {tab === 'security' && !isNew && (
        <UserSecurityTab
          userId={id}
          canEdit={canEdit}
          onToast={showToast}
          // 2FA kurulumu yalnız kişinin KENDİ kartında sunulur; yönetici başkası adına
          // authenticator kuramaz, yalnız kapatabilir (cihaz kaybı senaryosu).
          currentUserId={getUserId()}
          isAdmin={isSuperAdmin()}
        />
      )}
      {tab === 'defaults' && !isNew && (
        <UserDefaultsTab userId={id} canEdit={canEdit} onToast={showToast} />
      )}
      {tab === 'devices' && !isNew && (
        <UserDevicesTab userId={id} />
      )}
      {tab === 'activity' && !isNew && (
        <UserActivityTab userId={id} />
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.firstName} {form.lastName}”</strong> kullanıcısını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default UserDetailPage;
